import * as React from 'react';
import { useState } from 'react';

import { personalQuizUrl } from '~api/urls';

import { GenerateQuestionsFromAoIAdapter } from '~analytics/adapters/GenerateQuestionsFromAoIAdapter';
import { handleAiRateLimitError } from '~teacher/components/QuizEditor/errorHandlers/aiRateLimitErrorHandler';
import { useQuizByActivityId, useQuizLimit } from '~teacher/hook';
import { useGetActivityInsights } from '~teacher/hook/activity/insights/useGetActivityInsights';
import { useGenerateQuestionsByAreasOfImprovement } from '~teacher/hook/ai/useGenerateQuestionsByAreasOfImprovement';

import {
  SelectAreas,
  SelectAreasLoading,
  useSelectAreasReducer,
} from './SelectAreas';
import { SelectQuestions, SelectQuestionsLoading } from './SelectQuestions';

export type AreasOfImprovementStep =
  | 'step1-select-insights'
  | 'step2-select-questions';

interface AreasOfImprovementProps {
  backToGenerationType?: () => void;
  activityInstanceId: number;
  onClose: () => void;
}

export function AreasOfImprovement({
  backToGenerationType,
  activityInstanceId,
  onClose,
}: AreasOfImprovementProps) {
  const quizLimitQuery = useQuizLimit();
  const generateQuestionsMutation = useGenerateQuestionsByAreasOfImprovement();
  const [areasOfImprovementStep, setAreaOfImprovementStep] =
    useState<AreasOfImprovementStep>('step1-select-insights');

  const [
    {
      insightIndexes,
      questionCount,
      generateExplanations,
      isGeneratingQuestions,
    },
    selectInsightsDispatch,
  ] = useSelectAreasReducer();

  const activityInsightsQuery = useGetActivityInsights({
    activityInstanceId,
    select: (data) =>
      data.insights.insights.map(
        ({ description, topic, index, questionIds }) => ({
          description,
          topic,
          index,
          questionIds,
        })
      ),
  });
  const quizByActivityQuery = useQuizByActivityId({
    activityInstanceId: activityInstanceId,
    quizUrl: personalQuizUrl,
  });

  const generateQuestions = () => {
    selectInsightsDispatch({ type: 'START_GENERATING_QUESTIONS' });
    setAreaOfImprovementStep('step2-select-questions');

    // Send Area of Improvement event to Amplitude
    // We need number of areas of improvement, number of questions to generate. How many areas were selected amounts to generate

    new GenerateQuestionsFromAoIAdapter({
      num_aoi_generated: activityInsightsQuery.data?.length ?? 0,
      num_aoi_selected: insightIndexes.size,
      num_questions_to_generate: questionCount,
      generate_explanations: generateExplanations,
    }).sendAnalytics();

    generateQuestionsMutation.mutate(
      {
        activityInstanceId,
        insightIndexes: [...insightIndexes],
        questionCount,
        generateExplanations,
      },
      {
        onSettled: () => {
          selectInsightsDispatch({ type: 'STOP_GENERATING_QUESTIONS' });
        },
        onError: (error: unknown) => {
          setAreaOfImprovementStep('step1-select-insights');
          handleAiRateLimitError(error);
        },
      }
    );
  };

  switch (areasOfImprovementStep) {
    case 'step1-select-insights': {
      if (
        activityInsightsQuery.data === undefined ||
        quizByActivityQuery.data === undefined
      ) {
        return (
          <SelectAreasLoading backToGenerationType={backToGenerationType} />
        );
      }

      return (
        <SelectAreas
          generateQuestions={generateQuestions}
          backToGenerationType={backToGenerationType}
          insights={activityInsightsQuery.data}
          questions={quizByActivityQuery.data.quizQuestion}
          dispatch={selectInsightsDispatch}
          insightIndexes={insightIndexes}
          questionCount={questionCount}
          generateExplanations={generateExplanations}
          isGeneratingQuestions={isGeneratingQuestions}
        />
      );
    }
    case 'step2-select-questions': {
      if (generateQuestionsMutation.isLoading || quizLimitQuery.isLoading) {
        return (
          <SelectQuestionsLoading
            questionCount={questionCount}
            onBack={() => {
              generateQuestionsMutation.reset();
              setAreaOfImprovementStep('step1-select-insights');
            }}
          />
        );
      }
      return (
        <SelectQuestions
          source="areas_of_improvement"
          currentQuizCount={quizLimitQuery.data?.currentQuizCount ?? 0}
          limit={quizLimitQuery.data?.limit ?? Infinity}
          questions={(generateQuestionsMutation.data?.questions ?? []).map(
            (
              { questionText, questionType, explanation, answers = [] },
              index
            ) => ({
              id: `${index}`,
              text: questionText,
              questionType,
              explanation,
              answers: answers.map(({ order, text, isCorrect }) => ({
                order,
                text,
                isCorrect,
              })),
            })
          )}
          onBack={() => {
            generateQuestionsMutation.reset();
            setAreaOfImprovementStep('step1-select-insights');
          }}
          quizName={quizByActivityQuery.data?.quiz.name ?? ''}
          onClose={onClose}
        />
      );
    }
  }
}
